import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	viewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import {
	AreaChartConfig,
	isPaletteCustomOverridesActive,
} from '../../../models/chart-config.model';
import { AreaChartData } from '../../../models/chart-data.model';
import { ThemeConfig } from '../../../models/theme.model';
import { DataService } from '../../../services/data.service';
import {
	highchartsPaletteForThemeMode,
	pinHighchartsPaletteForThemeMode,
} from '@framework/analytics/utils/highcharts-palette-for-theme.util';
import { resolveChartThemeMode } from '@framework/analytics/utils/resolve-chart-theme-mode.util';
import { PaletteService } from '../../../services/palette.service';
import { ThemeMode, ThemeService } from '../../../services/theme.service';
import Highcharts from '../highcharts.config';
import { parseChartVisualizationInput } from '../../../utils/parse-chart-visualization-input.util';
import {
	resolveHighchartsCssColors,
	createHighchartsResizeObserver,
	reflowHighchartsToContainer,
	scheduleHighchartsReflowAfterLayout,
} from '../../../utils/resolve-highcharts-css-colors.util';
import { applyLegendLayoutToChartOptions } from '../../../utils/chart-legend-scale.util';
import { ChartRenderCoordinator } from '../../../utils/chart-render-coordinator.util';
import {
	areaChartDefaults,
	createAreaChartFillColor,
} from './jepto-area-chart.defaults';
import {
	coerceLineChartStringX,
	LINE_CHART_MIN_LIKELY_TIMESTAMP_MS,
} from '../jepto-line-chart/line-chart-string-x.util';

interface AreaChartDataPoint {
	x: number | string;
	y: number;
}

interface AreaChartSeries {
	name: string;
	data: AreaChartDataPoint[];
	color?: string;
	label?: string;
	aggregationValue?: number;
	comparisonAggregationValue?: number;
	comparisonData?: any[];
	custom?: any;
}

interface ChartClickEvent {
	point: { x: number | string; y: number; series: string };
	chart: { type: string; id?: string };
}

/**
 * Area Chart Component with signal-based reactivity
 */
@Component({
	selector: 'jepto-area-chart',
	templateUrl: './jepto-area-chart.component.html',
	styleUrls: ['./jepto-area-chart.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule],
})
export class JeptoAreaChartComponent implements AfterViewInit, OnDestroy {
	// Signal inputs
	cardId = input<string>();
	config = input<AreaChartConfig>();
	data = input<AreaChartData>();
	themeMode = input<ThemeMode>('auto');
	palette = input<string>('pal_jepto');
	themeConfig = input<ThemeConfig>();
	height = input<string>('100%');
	width = input<string>('100%');
	showLegend = input<boolean>(true);
	enableTooltip = input<boolean>(true);
	fillOpacity = input<number>(0.3);
	stacked = input<boolean>(false);

	// Outputs
	chartClick = output<ChartClickEvent>();
	seriesClick = output<ChartClickEvent>();

	// View queries
	private chartContainer = viewChild.required<ElementRef>('chartContainer');

	// Services
	private themeService = inject(ThemeService);
	private paletteService = inject(PaletteService);
	private dataService = inject(DataService, { optional: true });

	// Internal state
	private chart?: Highcharts.Chart;
	private resizeObserver?: ResizeObserver;
	private initialized = false;
	private renderCoordinator = new ChartRenderCoordinator();

	// Computed signals
	private effectiveData = computed(() => {
		const direct = this.data();
		if (direct) return direct;

		const id = this.cardId();
		if (id && this.dataService) {
			const serviceData = this.dataService.cardDataSignal(id)();
			if (serviceData?.chartType === 'area')
				return serviceData as unknown as AreaChartData;
		}
		return null;
	});

	private paletteColors = computed(() => {
		const cfg = this.config();
		const paletteId = cfg?.palette?.selectedPaletteId ?? this.palette();
		const resolvedMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);

		const overrides = isPaletteCustomOverridesActive(cfg?.palette);
		const customLight = overrides ? cfg?.palette?.customLight : undefined;
		const customDark = overrides ? cfg?.palette?.customDark : undefined;

		const palette = this.paletteService.getPaletteById(paletteId);
		if (palette) {
			if (resolvedMode === 'dark' && customDark?.length) {
				return customDark;
			}
			if (resolvedMode === 'light' && customLight?.length) {
				return customLight;
			}
			return resolvedMode === 'dark' ? palette.dark : palette.light;
		}

		if (cfg?.palette?.selectedPaletteId) {
			return this.paletteService.getColorsForPaletteWithOverrides(
				cfg.palette.selectedPaletteId,
				customLight,
				customDark,
				resolvedMode
			);
		}
		return this.paletteService.getColorPalette(paletteId);
	});

	private chartData = computed(() => {
		const sourceData = this.effectiveData();
		if (!sourceData?.data?.series) return [];

		return sourceData.data.series.map(
			series =>
				({
					name: series.name,
					data: series.data.map((point, index) => {
						let x: number | string, y: number;
						if (Array.isArray(point)) {
							x = point[0];
							y = point[1];
						} else if (
							typeof point === 'object' &&
							point !== null
						) {
							x = (point as any).x;
							y = (point as any).y;
						} else return { x: index, y: 0 };
						if (x === null || x === undefined || x === '') {
							x = index;
						}
						if (typeof x === 'string') {
							x = coerceLineChartStringX(x);
						}
						return { x, y: Number(y) || 0 };
					}),
					color: (series as any).color,
					label: (series as any).label,
					aggregationValue: (series as any).aggregationValue,
					comparisonAggregationValue: (series as any)
						.comparisonAggregationValue,
					comparisonData: (series as any).comparisonData,
					custom: (series as any).custom || {},
				}) as AreaChartSeries
		);
	});

	/** Same rules as jepto-line-chart: avoid treating categorical/mixed X as unix ms. */
	private xAxisType = computed<'datetime' | 'category'>(() => {
		const data = this.chartData();
		const xValues = data.flatMap(s => s.data.map(p => p.x));
		if (xValues.length === 0) return 'datetime';
		const numericX = xValues.filter(
			(v): v is number => typeof v === 'number' && Number.isFinite(v)
		);
		if (numericX.length !== xValues.length || numericX.length === 0) {
			return 'category';
		}
		const looksLikeDatetime = numericX.some(
			v => v >= LINE_CHART_MIN_LIKELY_TIMESTAMP_MS
		);
		return looksLikeDatetime ? 'datetime' : 'category';
	});

	private chartOptions = computed(() => {
		const baseOptions = { ...areaChartDefaults };
		const cfg = this.config();
		const rawViz: unknown = cfg?.visualization;
		const visualizationForMerge =
			rawViz == null || rawViz === ''
				? undefined
				: typeof rawViz === 'string'
					? (parseChartVisualizationInput(
							rawViz
						) as Highcharts.Options)
					: (rawViz as Highcharts.Options);
		const vizColors = visualizationForMerge?.colors;
		const paletteColorsArr = this.paletteColors();
		const colors =
			Array.isArray(vizColors) && vizColors.length > 0
				? vizColors
				: paletteColorsArr;
		const seriesData = this.transformDataToSeries();
		const id = this.cardId();
		const xAxisType = this.xAxisType();

		this.themeConfig();
		const resolvedThemeMode = resolveChartThemeMode(
			this.themeMode(),
			this.themeService.getActiveThemeModeSignal()()
		);
		this.themeService.themeIdDisplaySignal();

		const componentOptions: Partial<Highcharts.Options> = {
			...highchartsPaletteForThemeMode(resolvedThemeMode),
			colors: colors.length > 0 ? colors : undefined,
			chart: {
				...baseOptions.chart,
				backgroundColor: 'var(--chart-background, transparent)',
			},
			title: {
				text: cfg?.title || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			subtitle: {
				text: cfg?.subtitle || '',
				style: { color: 'var(--chart-text-color, inherit)' },
			},
			xAxis: {
				...baseOptions.xAxis,
				type: xAxisType,
				title: {
					text: '',
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			yAxis: {
				...baseOptions.yAxis,
				title: {
					text: '',
					style: { color: 'var(--chart-text-color, inherit)' },
				},
			},
			legend: { ...baseOptions.legend, enabled: this.showLegend() },
			tooltip: { ...baseOptions.tooltip, enabled: this.enableTooltip() },
			plotOptions: {
				...baseOptions.plotOptions,
				area: {
					...baseOptions.plotOptions?.area,
					fillOpacity: this.fillOpacity(),
					stacking: this.stacked() ? 'normal' : undefined,
					point: {
						events: {
							click: (event: any) => {
								this.chartClick.emit({
									point: {
										x: event.point.x,
										y: event.point.y,
										series: event.point.series.name,
									},
									chart: { type: 'area', id },
								});
							},
						},
					},
				},
				series: {
					...baseOptions.plotOptions?.series,
					events: {
						click: (event: any) => {
							this.seriesClick.emit({
								point: {
									x: event.point?.x || 0,
									y: event.point?.y || 0,
									series: event.target.name,
								},
								chart: { type: 'area', id },
							});
						},
					},
				},
			},
			series: seriesData,
		};

		let merged = Highcharts.merge(baseOptions, componentOptions);
		merged.series = seriesData;

		if (visualizationForMerge) {
			merged = Highcharts.merge(merged, visualizationForMerge);
			const vizSeries = visualizationForMerge.series;
			if (
				Array.isArray(vizSeries) &&
				vizSeries.length > 0 &&
				seriesData.length > 0
			) {
				merged.series = seriesData.map((s, i) => {
					const viz = vizSeries[i] || {};
					const item = Highcharts.merge(
						s,
						viz
					) as Highcharts.SeriesOptionsType;
					(item as Highcharts.SeriesAreaOptions).data = (
						s as Highcharts.SeriesAreaOptions
					).data;
					if (
						viz &&
						(viz as Highcharts.SeriesOptionsType).type !== undefined
					) {
						(item as Highcharts.SeriesAreaOptions).type = (
							viz as Highcharts.SeriesAreaOptions
						).type;
					}
					return item;
				}) as Highcharts.SeriesOptionsType[];
			} else {
				merged.series = seriesData;
			}
		}

		return pinHighchartsPaletteForThemeMode(merged, resolvedThemeMode);
	});

	constructor() {
		effect(() => {
			const options = this.chartOptions();
			const data = this.chartData();
			if (this.initialized && this.chart && data.length > 0) {
				const chart = this.chart;
				this.renderCoordinator.schedule(() => {
					chart.destroy();
					this.renderChart(options);
				});
			}
		});
	}

	ngAfterViewInit(): void {
		this.initializeChart();
		this.setupResizeObserver();
		this.initialized = true;
	}

	ngOnDestroy(): void {
		this.renderCoordinator.cancel();
		this.resizeObserver?.disconnect();
		this.chart?.destroy();
	}

	private renderChart(options: Highcharts.Options): void {
		const container = this.chartContainer().nativeElement;
		const resolved = resolveHighchartsCssColors(
			applyLegendLayoutToChartOptions(options, container),
			container
		);
		this.chart = Highcharts.chart(container, resolved);
		reflowHighchartsToContainer(this.chart, container);
		scheduleHighchartsReflowAfterLayout(() => this.chart, container);
	}

	private initializeChart(): void {
		const container = this.chartContainer();
		if (container) {
			this.renderChart(this.chartOptions());
		}
	}

	private setupResizeObserver(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = createHighchartsResizeObserver(
			() => this.chart,
			this.chartContainer().nativeElement
		);
	}

	private transformDataToSeries(): Highcharts.SeriesOptionsType[] {
		const inputData = this.chartData();
		if (!inputData?.length) return [];
		const xAxisType = this.xAxisType();
		const paletteColorsArr = this.paletteColors();
		const fillOpacity = this.fillOpacity();

		return inputData.map((series, seriesIndex) => {
			const resolvedColor =
				series.color ||
				paletteColorsArr[seriesIndex % paletteColorsArr.length];
			const fillColor = createAreaChartFillColor(
				resolvedColor,
				fillOpacity
			);

			if (!series.data?.length) {
				return {
					type: 'area' as const,
					name: series.label || series.name,
					data: [],
					color: series.color || undefined,
					fillColor,
					fillOpacity: 1,
					aggregationValue: series.aggregationValue,
					comparisonAggregationValue:
						series.comparisonAggregationValue,
					comparisonData: series.comparisonData,
					custom: series.custom,
				} as any;
			}

			const seriesData = series.data.map((point, index) => {
				if (typeof point.x === 'string') {
					const ts = coerceLineChartStringX(point.x);
					return xAxisType === 'datetime'
						? [
								typeof ts === 'number' && Number.isFinite(ts)
									? ts
									: point.x || `Point ${index + 1}`,
								point.y,
							]
						: [point.x || `Point ${index + 1}`, point.y];
				}
				return xAxisType === 'datetime'
					? [point.x, point.y]
					: [
							point.x === null || point.x === undefined
								? `Point ${index + 1}`
								: String(point.x),
							point.y,
						];
			});

			const shouldSortByX =
				xAxisType === 'datetime' &&
				seriesData.every(
					p =>
						typeof p[0] === 'number' &&
						Number.isFinite(p[0] as number)
				);
			const normalizedSeriesData = shouldSortByX
				? [...seriesData].sort(
						(a, b) => (a[0] as number) - (b[0] as number)
					)
				: seriesData;

			return {
				type: 'area' as const,
				name: series.label || series.name,
				data: normalizedSeriesData,
				color: series.color || undefined,
				fillColor,
				fillOpacity: 1,
				aggregationValue: series.aggregationValue,
				comparisonAggregationValue: series.comparisonAggregationValue,
				comparisonData: series.comparisonData,
				custom: series.custom,
			} as any;
		});
	}
}
